// Related documents for the sources that have nothing in dim_documents / dim_metadata, which
// is every non-FAOSTAT domain (see missing_metadata.txt). Used by the Metadata panel only when
// the database returns no document for the domain, so a domain that gets loaded later simply
// stops falling back here. Keyed by the same code the panel receives as `dataSource` (the view
// name without its vw_/mvw_ prefix, upper-cased); rows use the dim_documents shape so the
// ranking that pins the metadata document first applies unchanged.
//
// An entry is either a list of documents or a function of the indicator's config (the item
// from CCAsections: item_code, indicator_code, element_code...) returning one, for sources
// whose metadata is published per series or per SDG indicator rather than per database.
//
// URLs checked on 2026-09-16. ilostat.ilo.org, lpi.worldbank.org, datahub.itu.int and
// data.unicef.org refuse automated requests (403) and foodsystemsdashboard.org rate-limits
// them (429), so those pages were confirmed through search results rather than fetched.

const codesOf = (value) => [].concat(value ?? []).filter(Boolean).map(String);

// ---------------------------------------------------------------------------------------------
// UN Statistics Division: one metadata sheet per SDG indicator, at a predictable URL. A few
// indicators have sub-sheets (a/b) and no plain one; those are spelled out.
const SDG_SHEET_FILE = {
    "1.1.1": "01-01-01a",
    "1.3.1": "01-03-01a",
    "2.5.1": "02-05-01a",
    "2.5.1a": "02-05-01a",
    "2.5.1b": "02-05-01b",
    "12.3.1": "12-03-01b",
    "12.3.1a": "12-03-01a",
    "12.3.1b": "12-03-01b",
};
const SDG_TITLES = {
    "1.1.1": "Proportion of the population living below the international poverty line",
    "1.3.1": "Proportion of population covered by social protection floors/systems",
    "1.5.2": "Direct economic loss attributed to disasters",
    "2.5.1a": "Number of plant genetic resources for food and agriculture secured in conservation facilities",
    "2.5.1b": "Number of animal genetic resources for food and agriculture secured in conservation facilities",
    "2.5.2": "Proportion of local breeds classified as being at risk of extinction",
    "3.8.1": "Coverage of essential health services",
    "4.6.1": "Proportion of population achieving at least a fixed level of proficiency in functional literacy and numeracy skills",
    "5.4.1": "Proportion of time spent on unpaid domestic and care work, by sex, age and location",
    "5.b.1": "Proportion of individuals who own a mobile telephone, by sex",
    "6.3.2": "Proportion of bodies of water with good ambient water quality",
    "6.4.2": "Level of water stress: freshwater withdrawal as a proportion of available freshwater resources",
    "7.1.1": "Proportion of population with access to electricity",
    "8.1.1": "Annual growth rate of real GDP per capita",
    "8.3.1": "Proportion of informal employment in total employment, by sector and sex",
    "8.5.2": "Unemployment rate, by sex, age and persons with disabilities",
    "8.7.1": "Proportion and number of children aged 5-17 years engaged in child labour, by sex and age",
    "9.1.1": "Proportion of the rural population who live within 2 km of an all-season road",
    "10.3.1": "Proportion of population reporting having personally felt discriminated against or harassed",
    "10.4.2": "Redistributive impact of fiscal policy",
    "12.3.1b": "Food waste index",
    "15.1.2": "Proportion of important sites for terrestrial and freshwater biodiversity that are covered by protected areas",
    "16.10.2": "Number of countries that adopt and implement constitutional, statutory and/or policy guarantees for public access to information",
    "16.a.1": "Existence of independent national human rights institutions in compliance with the Paris Principles",
    "17.8.1": "Proportion of individuals using the Internet",
    "17.14.1": "Number of countries with mechanisms in place to enhance policy coherence of sustainable development",
};
const SDG_METADATA = (indicator, title = SDG_TITLES[indicator]) => {
    const file = SDG_SHEET_FILE[indicator]
        ?? indicator.replace(/[ab]$/, "").split(".").map((p) => p.padStart(2, "0")).join("-");
    return {
        file_title: `SDG indicator ${indicator} metadata${title ? ` - ${title}` : ""} (UN Statistics Division)`,
        file_name: `https://unstats.un.org/sdgs/metadata/files/Metadata-${file}.pdf`,
        source: "UNSD",
    };
};
const UNSD_METADATA_REPOSITORY = {
    file_title: "UN Statistics Division - SDG indicators metadata repository",
    file_name: "https://unstats.un.org/sdgs/metadata/",
    source: "UNSD",
};
const FAO_SDG_PORTAL = {
    file_title: "FAO - Sustainable Development Goals data portal: indicators under FAO custodianship",
    file_name: "https://www.fao.org/sustainable-development-goals-data-portal/data/indicators/en",
    source: "FAO",
};

// ---------------------------------------------------------------------------------------------
// ILO
const ILOSTAT_LFS = {
    file_title: "ILOSTAT - Labour Force Statistics (LFS and RURBAN databases): description and methodology",
    file_name: "https://ilostat.ilo.org/methods/concepts-and-definitions/description-labour-force-statistics/",
    source: "ILOSTAT",
};
const ILOSTAT_CONCEPTS = {
    file_title: "ILOSTAT - Concepts and definitions in labour statistics",
    file_name: "https://ilostat.ilo.org/methods/concepts-and-definitions/",
    source: "ILOSTAT",
};
const ILOSTAT_UNEMPLOYMENT = {
    file_title: "ILOSTAT - Statistics on unemployment and labour underutilization",
    file_name: "https://ilostat.ilo.org/topics/unemployment-and-labour-underutilization/",
    source: "ILOSTAT",
};
const ILOSTAT_INFORMALITY_SNAPSHOT = {
    file_title: "ILOSTAT - Informal employment rate: indicator snapshot",
    file_name: "https://ilostat.ilo.org/data/snapshots/informal-employment-rate/",
    source: "ILOSTAT",
};

// ---------------------------------------------------------------------------------------------
// World Bank. WDI publishes metadata per series in the DataBank glossary, so the documents
// differ from one indicator to the next within the same domain. The names are the ones the
// World Bank API returns for each code (checked 2026-09-16).
const WDI_SERIES = {
    "NY.GDP.MKTP.KD": "GDP (constant 2015 US$)",
    "NY.GDP.MKTP.KD.ZG": "GDP growth (annual %)",
    "NY.GDP.PCAP.KD": "GDP per capita (constant 2015 US$)",
    "NY.GDP.PCAP.PP.KD": "GDP per capita, PPP (constant 2021 international $)",
    "NY.GDP.PCAP.KD.ZG": "GDP per capita growth (annual %)",
    "NY.GDP.DEFL.KD.ZG": "Inflation, GDP deflator (annual %)",
    "NE.CON.GOVT.ZS": "General government final consumption expenditure (% of GDP)",
    "DT.TDS.DECT.GN.ZS": "Total debt service (% of GNI)",
    "TM.VAL.FOOD.ZS.UN": "Food imports (% of merchandise imports)",
    "TX.VAL.FOOD.ZS.UN": "Food exports (% of merchandise exports)",
    "SP.POP.0014.TO.ZS": "Population ages 0-14 (% of total population)",
    "SP.RUR.TOTL.ZS": "Rural population (% of total population)",
    "BX.TRF.PWKR.DT.GD.ZS": "Personal remittances, received (% of GDP)",
    "SL.UEM.TOTL.ZS": "Unemployment, total (% of total labor force) (modeled ILO estimate)",
    "EG.ELC.ACCS.ZS": "Access to electricity (% of population)",
    "EG.ELC.ACCS.UR.ZS": "Access to electricity, urban (% of urban population)",
    "EG.ELC.ACCS.RU.ZS": "Access to electricity, rural (% of rural population)",
    "IT.CEL.SETS": "Mobile cellular subscriptions",
    "SH.UHC.SRVS.CV.XD": "UHC service coverage index",
    "SI.POV.GINI": "Gini index",
    "SI.POV.DDAY": "Poverty headcount ratio at $3.00 a day (2021 PPP) (% of population)",
    "SI.POV.LMIC": "Poverty headcount ratio at $4.20 a day (2021 PPP) (% of population)",
    "SI.POV.UMIC": "Poverty headcount ratio at $8.30 a day (2021 PPP) (% of population)",
    "IQ.SPI.OVRL": "Statistical performance indicators (SPI): Overall score (scale 0-100)",
    "IQ.SPI.PIL1": "Statistical performance indicators (SPI): Pillar 1 data use score (scale 0-100)",
    "IQ.SPI.PIL2": "Statistical performance indicators (SPI): Pillar 2 data services score (scale 0-100)",
    "IQ.SPI.PIL3": "Statistical performance indicators (SPI): Pillar 3 data products score (scale 0-100)",
    "IQ.SPI.PIL4": "Statistical performance indicators (SPI): Pillar 4 data sources score (scale 0-100)",
    "IQ.SPI.PIL5": "Statistical performance indicators (SPI): Pillar 5 data infrastructure score (scale 0-100)",
    "FX.OWN.TOTL.ZS": "Account ownership at a financial institution or with a mobile-money-service provider (% of population ages 15+)",
    "FX.OWN.TOTL.MA.ZS": "Account ownership at a financial institution or with a mobile-money-service provider, male (% of population ages 15+)",
    "FX.OWN.TOTL.FE.ZS": "Account ownership at a financial institution or with a mobile-money-service provider, female (% of population ages 15+)",
};
// The view carries the UHC index under a code the World Bank no longer publishes under.
const WDI_CODE_ALIAS = { "SH.UHC.SCI": "SH.UHC.SRVS.CV.XD" };
const wdiSeriesDocument = (code) => {
    const wbCode = WDI_CODE_ALIAS[code] ?? code;
    const name = WDI_SERIES[wbCode] ?? WDI_SERIES[code] ?? wbCode;
    return {
        file_title: `World Bank WDI - series metadata: ${name} [${wbCode}]`,
        file_name: `https://databank.worldbank.org/metadataglossary/world-development-indicators/series/${wbCode}`,
        source: "World Bank",
    };
};
const WDI_ABOUT = {
    file_title: "World Bank - World Development Indicators: about the database, sources and methodology",
    file_name: "https://datatopics.worldbank.org/world-development-indicators/",
    source: "World Bank",
};
// SDG indicators that the platform reads from WDI rather than from the SDG database.
const WDI_SDG_INDICATOR = {
    "NY.GDP.PCAP.KD.ZG": "8.1.1",
    "EG.ELC.ACCS.ZS": "7.1.1",
    "SH.UHC.SCI": "3.8.1",
    "SI.POV.GINI": "10.4.2",
    "SI.POV.DDAY": "1.1.1",
};

// ---------------------------------------------------------------------------------------------
// Other sources
const AQUASTAT_METHODOLOGY = {
    file_title: "FAO AQUASTAT - methodology",
    file_name: "https://www.fao.org/aquastat/en/overview/methodology/",
    source: "AQUASTAT",
};
const AQUASTAT_GLOSSARY = {
    file_title: "FAO AQUASTAT - glossary of variables and definitions",
    file_name: "https://www.fao.org/aquastat/en/databases/glossary/",
    source: "AQUASTAT",
};
const FISHSTAT_STATISTICS = {
    file_title: "FAO Fisheries and Aquaculture - statistics: FishStat databases, methodology and standards",
    file_name: "https://www.fao.org/fishery/en/statistics",
    source: "FishStat",
};
const ASFIS_ISSCAAP = {
    file_title: "FAO - ASFIS list of species and ISSCAAP classification (description)",
    file_name: "https://www.fao.org/fishery/en/collection/asfis",
    source: "FishStat",
};
const FRA_TERMS = {
    file_title: "FAO - Global Forest Resources Assessment 2025: terms and definitions (FRA Working Paper 194)",
    file_name: "https://openknowledge.fao.org/server/api/core/bitstreams/a6e225da-4a31-4e06-818d-ca3aeadfd635/content",
    source: "FRA",
};
const FRA_PLATFORM = {
    file_title: "FAO - Global Forest Resources Assessment: FRA data platform",
    file_name: "https://fra-data.fao.org/",
    source: "FRA",
};
const FRA_2025_REPORT = {
    file_title: "FAO - Global Forest Resources Assessment 2025: main report",
    file_name: "https://openknowledge.fao.org/handle/20.500.14283/cd6709en",
    source: "FRA",
};
const FSD_METHODOLOGY = {
    file_title: "Food Systems Dashboard - data sources and methodology",
    file_name: "https://www.foodsystemsdashboard.org/information/data-sources-and-methodology",
    source: "Food Systems Dashboard",
};
const FSD_INDICATORS = {
    file_title: "Food Systems Dashboard - indicator descriptions",
    file_name: "https://www.foodsystemsdashboard.org/indicators",
    source: "Food Systems Dashboard",
};
const UIS_GLOSSARY = {
    file_title: "UNESCO Institute for Statistics - glossary of indicator definitions",
    file_name: "https://databrowser.uis.unesco.org/resources/glossary",
    source: "UNESCO UIS",
};
const UIS_METHODOLOGY = {
    file_title: "UNESCO Institute for Statistics - methods and methodology",
    file_name: "https://uis.unesco.org/en/methodology",
    source: "UNESCO UIS",
};
const RULIS_TECHNICAL_NOTES = {
    file_title: "FAO RuLIS - technical notes on concepts and definitions used for the indicators",
    file_name: "https://openknowledge.fao.org/server/api/core/bitstreams/db762578-cc39-4ae0-a61d-e56f92ea14db/content",
    source: "RuLIS",
};

export const RELATED_DOCUMENTS_FALLBACK = {
    // ----- ILO -----
    // Employment-to-population ratio by sex, age and rural/urban areas (EMP_DWAP_SEX_AGE_GEO_RT)
    ILO_DWAP: [
        ILOSTAT_LFS,
        {
            file_title: "ILOSTAT - Employment-to-population ratio: indicator snapshot",
            file_name: "https://ilostat.ilo.org/data/snapshots/employment-to-population-ratio/",
            source: "ILOSTAT",
        },
        ILOSTAT_CONCEPTS,
    ],
    // Time-related underemployment rate by sex, age and rural/urban areas (TRU_DEMP_SEX_AGE_GEO_RT)
    ILO_TRU: [ILOSTAT_LFS, ILOSTAT_UNEMPLOYMENT, ILOSTAT_CONCEPTS],
    // Unemployment rate by sex, age and rural/urban areas (UNE_2EAP_SEX_AGE_GEO_RT), SDG 8.5.2
    ILO_UNEMP: [SDG_METADATA("8.5.2"), ILOSTAT_LFS, ILOSTAT_UNEMPLOYMENT],
    // Informal employment rate by sex, age and rural/urban areas (EMP_NIFL_SEX_AGE_GEO_RT), SDG 8.3.1
    ILO_NIFL: [SDG_METADATA("8.3.1"), ILOSTAT_LFS, ILOSTAT_INFORMALITY_SNAPSHOT],
    // SDG 8.3.1 informal employment by sex and economic activity (SDG_0831_SEX_ECO_RT)
    ILO_SDG8: [
        SDG_METADATA("8.3.1"),
        {
            file_title: "ILOSTAT - Statistics on the informal economy",
            file_name: "https://ilostat.ilo.org/topics/informality/",
            source: "ILOSTAT",
        },
        ILOSTAT_INFORMALITY_SNAPSHOT,
    ],
    // SDG 8.7.1 child labour, agriculture (mvw_ilo_sdg87)
    ILO_SDG87: [SDG_METADATA("8.7.1"), ILOSTAT_CONCEPTS],

    // ----- World Bank -----
    // One glossary page per series, the SDG sheet where the series backs an SDG indicator,
    // then the database description.
    WB_WDI: (info) => {
        const codes = codesOf(info?.item_code);
        const sdg = codes.map((code) => WDI_SDG_INDICATOR[code]).filter(Boolean).map((i) => SDG_METADATA(i));
        return [...sdg, ...codes.map(wdiSeriesDocument), WDI_ABOUT];
    },
    WB_WGI: [
        {
            file_title: "World Bank - The Worldwide Governance Indicators: methodology and analytical issues (Kaufmann, Kraay, Mastruzzi)",
            file_name: "https://www.worldbank.org/content/dam/sites/govindicators/doc/wgimethodologypaper.pdf",
            source: "World Bank",
        },
        {
            file_title: "World Bank - Worldwide Governance Indicators: documentation",
            file_name: "https://www.worldbank.org/en/publication/worldwide-governance-indicators/documentation",
            source: "World Bank",
        },
    ],
    WB_LPI: [
        {
            file_title: "World Bank - Logistics Performance Index: series metadata [LP.LPI.OVRL.XQ]",
            file_name: "https://databank.worldbank.org/metadataglossary/world-development-indicators/series/LP.LPI.OVRL.XQ",
            source: "World Bank",
        },
        {
            file_title: "World Bank - Logistics Performance Index 2023 (Connecting to Compete): report and methodology",
            file_name: "https://lpi.worldbank.org/",
            source: "World Bank",
        },
    ],
    WB_RAI: [
        SDG_METADATA("9.1.1"),
        {
            file_title: "World Bank - Rural Access Index (RAI): dataset description and methodology",
            file_name: "https://datacatalog.worldbank.org/search/dataset/0038250",
            source: "World Bank",
        },
    ],
    WB_ES_OBSTACLES: [
        {
            file_title: "World Bank - Enterprise Surveys: methodology",
            file_name: "https://www.enterprisesurveys.org/en/methodology",
            source: "World Bank",
        },
        {
            file_title: "World Bank - Enterprise Surveys: biggest obstacle (topic description and data)",
            file_name: "https://www.enterprisesurveys.org/en/data/exploretopics/biggest-obstacle",
            source: "World Bank",
        },
    ],
    ASPIRE: [
        {
            file_title: "World Bank - ASPIRE: documentation and methodology",
            file_name: "https://www.worldbank.org/en/data/datatopics/aspire/documentation",
            source: "World Bank",
        },
        {
            file_title: "World Bank - ASPIRE: indicators at a glance (coverage, adequacy, benefit incidence)",
            file_name: "https://www.worldbank.org/en/data/datatopics/aspire/indicator-glance",
            source: "World Bank",
        },
    ],

    // ----- SDG global database (UNSD mirror) -----
    // vw_sdg_full carries the indicator number in element_code; vw_unsd in indicator_code.
    // 2.5.1 has one sheet for plant and one for animal genetic resources, told apart by the
    // series codes; 12.3.1 on the platform is the food waste half.
    SDG_FULL: (info) => {
        const indicator = codesOf(info?.element_code)[0];
        if (!indicator) return [UNSD_METADATA_REPOSITORY];
        const items = codesOf(info?.item_code).join(" ");
        const sheet = indicator === "2.5.1"
            ? (/ANIM/.test(items) ? "2.5.1b" : "2.5.1a")
            : indicator === "12.3.1" ? "12.3.1b" : indicator;
        return [SDG_METADATA(sheet), UNSD_METADATA_REPOSITORY];
    },
    UNSD: (info) => {
        const indicator = codesOf(info?.indicator_code)[0];
        return [...(indicator ? [SDG_METADATA(indicator)] : []), UNSD_METADATA_REPOSITORY];
    },
    // SDG 2.5.2 breeds at risk of extinction (FAO custodian, DAD-IS)
    SDG: [SDG_METADATA("2.5.2"), FAO_SDG_PORTAL, UNSD_METADATA_REPOSITORY],

    // ----- FAO, non-FAOSTAT -----
    AQUA: (info) => {
        const items = codesOf(info?.item_code);
        // 4550 is the SDG 6.4.2 water stress series.
        const sdg = items.includes("4550") ? [SDG_METADATA("6.4.2"), FAO_SDG_PORTAL] : [];
        return [...sdg, AQUASTAT_METHODOLOGY, AQUASTAT_GLOSSARY, {
            file_title: "FAO AQUASTAT - database",
            file_name: "https://data.apps.fao.org/aquastat/?lang=en",
            source: "AQUASTAT",
        }];
    },
    FISHSTAT_PRODUCTION: [
        {
            file_title: "FAO FishStat - Global production by production source: collection description and methodology",
            file_name: "https://www.fao.org/fishery/en/collection/global_production",
            source: "FishStat",
        },
        {
            file_title: "FAO FishStat - Global capture production: collection description",
            file_name: "https://www.fao.org/fishery/en/collection/capture",
            source: "FishStat",
        },
        {
            file_title: "FAO FishStat - Global aquaculture production: collection description",
            file_name: "https://www.fao.org/fishery/en/collection/aquaculture",
            source: "FishStat",
        },
        ASFIS_ISSCAAP,
        FISHSTAT_STATISTICS,
    ],
    FISHSTAT_PROCESSED: [
        {
            file_title: "FAO FishStat - Global production of processed products: collection description and methodology",
            file_name: "https://www.fao.org/fishery/en/collection/global_commodity_prod",
            source: "FishStat",
        },
        ASFIS_ISSCAAP,
        FISHSTAT_STATISTICS,
    ],
    FISHSTAT_TRADE: [
        {
            file_title: "FAO FishStat - Global trade of aquatic products: collection description and methodology",
            file_name: "https://www.fao.org/fishery/en/collection/global_commodity_trad",
            source: "FishStat",
        },
        FISHSTAT_STATISTICS,
    ],
    FRA: [FRA_TERMS, FRA_2025_REPORT, FRA_PLATFORM],
    GIEWS_ASI: [
        {
            file_title: "FAO GIEWS - Agricultural Stress Index System (ASIS): methodology and description",
            file_name: "https://www.fao.org/giews/earthobservation/asistool.jsp?lang=en",
            source: "GIEWS",
        },
        {
            file_title: "FAO GIEWS - Earth Observation for crop monitoring",
            file_name: "https://www.fao.org/giews/earthobservation/index.jsp",
            source: "GIEWS",
        },
        {
            file_title: "FAO - Agricultural stress index annual summary (Global, 1 km): dataset metadata",
            file_name: "https://data.apps.fao.org/catalog/iso/2afba815-0402-400a-bcf8-cf0959f19421",
            source: "FAO",
        },
    ],
    INFARM: [
        {
            file_title: "FAO - InFARM: International FAO Antimicrobial Resistance Monitoring system, description",
            file_name: "https://www.fao.org/antimicrobial-resistance/resources/infarm-system/en/",
            source: "FAO",
        },
        {
            file_title: "FAO - Antimicrobial resistance: surveillance and monitoring",
            file_name: "https://www.fao.org/antimicrobial-resistance/key-sectors/surveillance-and-monitoring/en/",
            source: "FAO",
        },
    ],
    RULIS: [
        RULIS_TECHNICAL_NOTES,
        {
            file_title: "FAO RuLIS - codebook",
            file_name: "https://www.fao.org/3/ca9548en/ca9548en.pdf",
            source: "RuLIS",
        },
        {
            file_title: "FAO RuLIS - user guide",
            file_name: "https://openknowledge.fao.org/server/api/core/bitstreams/15225a81-755a-414c-916c-67e579c89add/content",
            source: "RuLIS",
        },
        {
            file_title: "FAO - Rural Livelihoods Information System (RuLIS)",
            file_name: "https://www.fao.org/in-action/rural-livelihoods-dataset-rulis/en",
            source: "RuLIS",
        },
    ],
    SOFA: [
        {
            file_title: "FAO - The State of Food and Agriculture 2023: revealing the true cost of food to transform agrifood systems (methodology of the hidden-cost estimates)",
            file_name: "https://openknowledge.fao.org/handle/20.500.14283/cc7724en",
            source: "FAO",
        },
        {
            file_title: "FAO - The State of Food and Agriculture 2023: publication page",
            file_name: "https://www.fao.org/publications/home/fao-flagship-publications/the-state-of-food-and-agriculture/2023/en",
            source: "FAO",
        },
    ],
    // mvw_tcli is derived from the FAOSTAT Trade Indicators domain (TI), whose metadata sheet is
    // hosted on files-faostat like the ones dim_documents points to.
    TCLI: [
        {
            file_title: "FAOSTAT - Trade Indicators: metadata",
            file_name: "https://files-faostat.fao.org/production/TI/TI_e.pdf",
            source: "FAOSTAT",
        },
        {
            file_title: "FAOSTAT - Trade Indicators domain",
            file_name: "https://www.fao.org/faostat/en/#data/TI/metadata",
            source: "FAOSTAT",
        },
        {
            file_title: "FAOSTAT analytical brief - trade of agricultural commodities",
            file_name: "https://openknowledge.fao.org/server/api/core/bitstreams/7920da21-9d82-4651-a748-13e2b7733e7c/content",
            source: "FAOSTAT",
        },
    ],
    FSS: [
        {
            file_title: "UN Food Systems Coordination Hub - Member State dialogue convenors and national pathways",
            file_name: "https://www.unfoodsystemshub.org/member-state-dialogue/dialogues-and-pathways/en",
            source: "UN Food Systems Hub",
        },
        {
            file_title: "Food Systems Summit Dialogues - country information and pathways",
            file_name: "https://summitdialogues.org/countries/",
            source: "UN Food Systems Hub",
        },
    ],

    // ----- Other UN agencies and partners -----
    FSD: [FSD_METHODOLOGY, FSD_INDICATORS],
    GSMA_MCI: [
        SDG_METADATA("5.b.1"),
        {
            file_title: "GSMA - Mobile Connectivity Index: methodology and data",
            file_name: "https://www.mobileconnectivityindex.com/",
            source: "GSMA",
        },
    ],
    IDMC: [
        {
            file_title: "IDMC - Global Internal Displacement Database: methodology",
            file_name: "https://www.internal-displacement.org/database/methodology/",
            source: "IDMC",
        },
        {
            file_title: "IDMC - Global Internal Displacement Database: displacement data",
            file_name: "https://www.internal-displacement.org/database/displacement-data/",
            source: "IDMC",
        },
    ],
    ITU: [
        SDG_METADATA("17.8.1"),
        {
            file_title: "ITU - ICT statistics: methodology and indicator definitions",
            file_name: "https://www.itu.int/en/ITU-D/Statistics/Pages/default.aspx",
            source: "ITU",
        },
        {
            file_title: "ITU DataHub - Individuals using the Internet: indicator page",
            file_name: "https://datahub.itu.int/data/?i=11624",
            source: "ITU",
        },
    ],
    SCP_HAT: [
        {
            file_title: "SCP-HAT 2.0 - technical documentation (methodology, incl. marine eutrophication)",
            file_name: "https://scp-hat.org/wp-content/uploads/2021/11/SCP-HAT-2.0_Technical-documentation_Oct2021_final02.pdf",
            source: "UNEP",
        },
        {
            file_title: "SCP-HAT - methods and data sources",
            file_name: "https://scp-hat.org/methods/",
            source: "UNEP",
        },
        {
            file_title: "UNEP - Sustainable Consumption and Production Hotspots Analysis Tool: factsheet",
            file_name: "https://www.unep.org/resources/factsheet/sustainable-consumption-and-production-hotspots-analysis-tool",
            source: "UNEP",
        },
    ],
    UNESCO_OPRI: [
        {
            file_title: "UNESCO UIS - Mean years of schooling: indicator metadata",
            file_name: "https://www.uis.unesco.org/sites/default/files/medias/fichiers/2025/07/Mean_years_of_schooling_final.pdf",
            source: "UNESCO UIS",
        },
        UIS_GLOSSARY,
        UIS_METHODOLOGY,
    ],
    UNESCO_SDG: [
        SDG_METADATA("4.6.1"),
        {
            file_title: "UNESCO UIS - Literacy rate: definition",
            file_name: "https://uis.unesco.org/en/glossary-term/literacy-rate",
            source: "UNESCO UIS",
        },
        UIS_GLOSSARY,
        UIS_METHODOLOGY,
    ],
    UNICEF: [
        {
            file_title: "WHO/UNICEF - Indicators for assessing infant and young child feeding practices: definitions and measurement methods (2021)",
            file_name: "https://www.who.int/publications/i/item/9789240018389",
            source: "WHO/UNICEF",
        },
        {
            file_title: "UNICEF Data - Infant and young child feeding: topic page and data",
            file_name: "https://data.unicef.org/topic/nutrition/infant-and-young-child-feeding/",
            source: "UNICEF",
        },
    ],
    UNPD: [
        {
            file_title: "UN DESA Population Division - World Population Prospects 2024: methodology report",
            file_name: "https://population.un.org/wpp/assets/Files/WPP2024_Methodology-Report_Final.pdf",
            source: "UNPD",
        },
        {
            file_title: "UN DESA Population Division - World Population Prospects 2024",
            file_name: "https://population.un.org/wpp/",
            source: "UNPD",
        },
    ],
    USDA: [
        {
            file_title: "USDA ERS - International Agricultural Productivity: documentation and methods",
            file_name: "https://www.ers.usda.gov/data-products/international-agricultural-productivity/documentation-and-methods",
            source: "USDA ERS",
        },
        {
            file_title: "USDA ERS - International Agricultural Productivity: data product",
            file_name: "https://www.ers.usda.gov/data-products/international-agricultural-productivity",
            source: "USDA ERS",
        },
    ],
};
